export type WebMessageRealtimeEvent = {
  type?: string;
  conversationId?: string;
  conversation_id?: string;
  senderUserId?: string | number;
  sender_user_id?: string | number;
  fromUserId?: string | number;
  from_user_id?: string | number;
  messageId?: string | number;
  message_id?: string | number;
  messageIds?: Array<string | number>;
  message_ids?: Array<string | number>;
  clientMessageId?: string;
  client_message_id?: string;
  clientMessageIds?: string[];
  client_message_ids?: string[];
  [key: string]: unknown;
};

function runtimeAuthHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const token =
    window.localStorage.getItem("intimsync.access_token") ||
    window.localStorage.getItem("access_token") ||
    window.localStorage.getItem("jwt") ||
    window.localStorage.getItem("admin_jwt") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ")
          ? token
          : `Bearer ${token}`,
      }
    : {};
}

export async function streamWebMessageEvents(
  conversationId: string,
  signal: AbortSignal,
  onEvent: (event: WebMessageRealtimeEvent) => void,
): Promise<void> {
  if (!conversationId) return;

  const q = new URLSearchParams({
    conversationId,
  });

  const authHeaders = runtimeAuthHeaders();

  console.log("[GlobalMessageDelivery] opening SSE", {
    conversationId,
    hasAuthorization: !!authHeaders.Authorization,
  });

  const response = await fetch(
    `/api/v1/messages/events?${q.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        ...authHeaders,
      },
      credentials: "include",
      signal,
    },
  );

  if (!response.ok || !response.body) {
    throw new Error(`message_events_http_${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";

  function consumeBlock(block: string) {
    const lines = block.split(/\r?\n/);

    let eventName = "message";
    const dataLines: string[] = [];

    for (const line of lines) {
      if (!line || line.startsWith(":")) continue;

      if (line.startsWith("event:")) {
        eventName =
          line.slice("event:".length).trim() || "message";
        continue;
      }

      if (line.startsWith("data:")) {
        dataLines.push(
          line.slice("data:".length).trimStart(),
        );
      }
    }

    if (!dataLines.length) return;

    const raw = dataLines.join("\n");

    try {
      const parsed = JSON.parse(raw);

      onEvent({
        ...parsed,
        type: parsed?.type || eventName,
      });
    } catch {
      onEvent({
        type: eventName,
        raw,
      });
    }
  }

  while (!signal.aborted) {
    const { value, done } = await reader.read();

    if (done) break;

    buffer += decoder.decode(value, {
      stream: true,
    });

    while (true) {
      const match = buffer.match(/\r?\n\r?\n/);

      if (!match || match.index === undefined) break;

      const block = buffer.slice(0, match.index);

      buffer = buffer.slice(
        match.index + match[0].length,
      );

      consumeBlock(block);
    }
  }
}
